import { Injectable, inject, makeEnvironmentProviders } from '@angular/core'
import { AlertsFacade } from '@navix/alerts/domain'
import { NotificationHandle, NotificationService } from '@navix/ui/common'

/**
 * Concrete {@link NotificationService} adapter that delegates to the app-wide
 * {@link AlertsFacade} (NgRx-backed legacy toast system).
 *
 * Wired once in the application commons so `ui`-scoped consumers depend only on
 * the abstract {@link NotificationService} while the toast mechanism remains
 * swappable through a single provider change.
 */
@Injectable()
export class AlertsFacadeNotificationService extends NotificationService {
  private readonly alertsFacade = inject(AlertsFacade)

  // Per-instance counter for unique progress-toast ids; combined with a fixed
  // prefix it yields a stable, collision-free id the handle can later remove.
  private seq = 0

  override success(message: string): void {
    this.alertsFacade.addSuccess({ message })
  }

  override error(message: string): void {
    this.alertsFacade.addError({ message })
  }

  override info(message: string): void {
    this.alertsFacade.addInfo({ message })
  }

  override warning(message: string): void {
    this.alertsFacade.addWarning({ message })
  }

  override progress(message: string): NotificationHandle {
    const id = `notification-progress-${++this.seq}`
    this.alertsFacade.addSuccess(
      { message, id },
      { isAutoClose: false, isDismisable: false, isProgress: true }
    )
    return { dismiss: () => this.alertsFacade.remove(id) }
  }
}

export function provideNotificationService() {
  return makeEnvironmentProviders([
    { provide: NotificationService, useClass: AlertsFacadeNotificationService }
  ])
}
